import { useCallback, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSWRConfig } from "swr";
import { IconFileDescription } from "@tabler/icons-react";
import { Heading, Text } from "../../../../components/common/typography";
import { SearchBar } from "../../../../components/common/input";
import { SegmentedControl } from "../../../../components/common/segmented-control";
import { Pagination } from "../../../../components/common/pagination";
import { ShimmerLoader, Shimmer } from "../../../../components/common/shimmer";
import { AppRoutes } from "../../../../common/routes/app_routes";
import { useQuotations, type QuotationFilter } from "../../hooks/useQuotations";
import { SalesQueryDtoSortOrderEnum, type SaleDto } from "../../../../api";
import { useRenewQuotation } from "../../../sales/hooks/useRenewQuotation";
import { deriveQuotationStatus } from "../../utils/quotation-status";
import { QuotationRow } from "../components/quotation-row";
import { QuotationsStats } from "../components/quotations-stats";
import { EmptyState } from "../../../../components/common/empty-state";
import { getQuotationCheckoutUrl } from "../../utils/get-quotation-checkout-url";
import { useDebouncedValue } from "../../../../hooks/useDebouncedValue";

const FILTER_OPTIONS = [
  { value: "all" as const, label: "All" },
  { value: "pending" as const, label: "Active" },
  { value: "paid" as const, label: "Paid" },
  { value: "expired" as const, label: "Expired" },
];

const PAGE_SIZE = 10;

// Quotation numbers are short (e.g. "S00034"); the cap keeps a pasted wall of
// text out of the request URL.
const SEARCH_MAX_LENGTH = 50;

// Search once typing pauses, not per keystroke — holding a key down otherwise
// fires a request per character and trips the API rate limit, which surfaced
// as "We couldn't load your quotations".
const SEARCH_DEBOUNCE_MS = 300;

const VALID_QUOTATION_FILTERS = new Set<QuotationFilter>(["all", "pending", "paid", "expired"]);

function parseQuotationFilter(v: string | null): QuotationFilter {
  return v && VALID_QUOTATION_FILTERS.has(v as QuotationFilter) ? (v as QuotationFilter) : "all";
}

export function QuotationsPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { mutate } = useSWRConfig();
  const { renewQuotation } = useRenewQuotation();

  const filter = parseQuotationFilter(params.get("status"));
  const search = (params.get("q") ?? "").slice(0, SEARCH_MAX_LENGTH);
  const debouncedSearch = useDebouncedValue(search, SEARCH_DEBOUNCE_MS);
  const page = Math.max(1, Number(params.get("page")) || 1);
  const [renewingIds, setRenewingIds] = useState<Set<number>>(new Set());

  const setParam = useCallback(
    (updates: Record<string, string | undefined>) => {
      setParams((prev) => {
        const next = new URLSearchParams(prev);
        for (const [k, v] of Object.entries(updates)) {
          if (v === undefined || v === "") next.delete(k);
          else next.set(k, v);
        }
        return next;
      });
    },
    [setParams],
  );

  const query = useMemo(
    () => ({
      page,
      pageSize: PAGE_SIZE,
      search: debouncedSearch.trim() || undefined,
      sortBy: "create_date",
      sortOrder: SalesQueryDtoSortOrderEnum.Desc,
    }),
    [page, debouncedSearch],
  );

  const { data, error, isLoading } = useQuotations(filter, query);

  const visibleItems = useMemo(() => {
    const items = data?.data ?? [];
    if (filter === "expired") {
      return items.filter(
        (q) => deriveQuotationStatus(q.state, q.validityDate) === "expired",
      );
    }
    if (filter === "pending") {
      return items.filter(
        (q) => deriveQuotationStatus(q.state, q.validityDate) === "pending",
      );
    }
    return items;
  }, [data, filter]);

  const pageCount = Math.max(1, data?.meta?.pageCount ?? 1);

  const handleFilter = useCallback((next: QuotationFilter) => {
    setParam({ status: next === "all" ? undefined : next, page: undefined });
  }, [setParam]);

  const handleSearch = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setParam({ q: e.target.value || undefined, page: undefined });
  }, [setParam]);

  const handleClearSearch = useCallback(() => {
    setParam({ q: undefined, page: undefined });
  }, [setParam]);

  const handleView = useCallback(
    (id: number) => navigate(AppRoutes.QUOTATION_DETAILS.replace(":id", String(id))),
    [navigate],
  );

  const handlePay = useCallback(
    (id: number) => navigate(getQuotationCheckoutUrl(id)),
    [navigate],
  );

  const handleRenew = useCallback(
    async (id: number) => {
      setRenewingIds((prev) => new Set(prev).add(id));
      const ok = await renewQuotation(id);
      setRenewingIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      if (ok) {
        await mutate((key) => typeof key === "string" && key.startsWith("quotations-"));
      }
    },
    [renewQuotation, mutate],
  );

  return (
    <div className="flex w-full flex-col gap-6 px-8 py-6 max-md:gap-4 max-md:px-4">
      <div className="flex flex-col gap-2">
        <Heading variant="h2" tone="dark">Quotations</Heading>
        <Text variant="bodySm" tone="muted">All your quotation requests in one place</Text>
      </div>

      <QuotationsStats />

      <div className="flex items-center justify-between gap-4 max-md:flex-col max-md:items-stretch max-md:gap-3">
        <SegmentedControl
          options={FILTER_OPTIONS}
          value={filter}
          onChange={handleFilter}
          aria-label="Filter quotations"
        />
        <div className="w-[800px] max-md:w-full">
          <SearchBar
            placeholder="Search by quotation number"
            value={search}
            onChange={handleSearch}
            onClear={handleClearSearch}
            maxLength={SEARCH_MAX_LENGTH}
          />
        </div>
      </div>

      <QuotationsList
        items={visibleItems}
        isLoading={isLoading}
        error={!!error}
        onView={handleView}
        onPay={handlePay}
        onRenew={handleRenew}
        renewingIds={renewingIds}
      />

      {pageCount > 1 && (
        <div className="flex justify-center py-2">
          <Pagination
            page={page}
            pageCount={pageCount}
            onPageChange={(p) => setParam({ page: p > 1 ? String(p) : undefined })}
          />
        </div>
      )}
    </div>
  );
}

interface QuotationsListProps {
  items: SaleDto[];
  isLoading: boolean;
  error: boolean;
  onView: (id: number) => void;
  onPay: (id: number) => void;
  onRenew: (id: number) => void;
  renewingIds: Set<number>;
}

function QuotationsList({
  items,
  isLoading,
  error,
  onView,
  onPay,
  onRenew,
  renewingIds,
}: QuotationsListProps) {
  if (isLoading) {
    return (
      <ShimmerLoader message="Loading your quotations…">
        <div className="flex flex-col gap-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <Shimmer key={i} className="h-[88px] w-full" />
          ))}
        </div>
      </ShimmerLoader>
    );
  }

  if (error) {
    return (
      <div className="rounded-xl border border-borderDefault bg-bgWhite px-6 py-8 text-center">
        <Text variant="bodyBold" tone="dark">We couldn't load your quotations.</Text>
        <Text variant="bodySm" tone="muted">Please try again in a moment.</Text>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <EmptyState
        icon={IconFileDescription}
        title="No quotations found"
        description="Quotations you've requested will appear here."
      />
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {items.map((sale) => (
        <QuotationRow
          key={sale.id}
          sale={sale}
          itemCount={sale.orderLineIds?.length ?? 0}
          onView={onView}
          onPay={onPay}
          onRenew={onRenew}
          isRenewing={renewingIds.has(sale.id)}
        />
      ))}
    </div>
  );
}
